// AdminHomeOptimized.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";


import { getFirestore, collection, onSnapshot, query, orderBy, where, limit, doc, updateDoc, getDocs, getAggregateFromServer, sum } from "firebase/firestore";
import { FaEdit, FaEye, FaTimes, FaSearch, FaCalendarDay, FaMoneyBillWave, FaStore, FaStoreSlash } from "react-icons/fa";


import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";   // <-- NUEVO
import "dayjs/locale/es";
import bgPdf from "../assets/bgPdf.png";


dayjs.extend(customParseFormat);

dayjs.extend(isBetween);    

dayjs.locale("es");



function numberToSpanish(n) {
  const unidades = ["cero", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve"];
  const especiales = [
    "diez","once","doce","trece","catorce","quince","dieciséis","diecisiete","dieciocho","diecinueve"
  ];
  const decenas = ["", "", "veinte", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"];
  const centenas = ["", "ciento", "doscientos", "trescientos", "cuatrocientos", "quinientos", "seiscientos", "setecientos", "ochocientos", "novecientos"];

  if (n < 10) return unidades[n];
  if (n < 20) return especiales[n - 10];
  if (n < 100) {
    const d = Math.floor(n / 10);
    const u = n % 10;
    if (n === 20) return "veinte";
    if (n < 30) return "veinti" + (u ? unidades[u] : "");
    return decenas[d] + (u ? " y " + unidades[u] : "");
  }
  if (n === 100) return "cien";
  if (n < 1000) {
    const c = Math.floor(n / 100);
    const r = n % 100;
    return (c === 1 ? "ciento" : centenas[c]) + (r ? " " + numberToSpanish(r) : "");
  }
  if (n < 1_000_000) {
    const miles = Math.floor(n / 1000);
    const r = n % 1000;
    const milesText = miles === 1 ? "mil" : numberToSpanish(miles) + " mil";
    return milesText + (r ? " " + numberToSpanish(r) : "");
  }
  return String(n);
}

// Convierte data.date (Timestamp, ISO string o "28 de junio de 2025, 4:43:47 p.m. UTC-6") a Date
function parsePaymentDate(raw) {
  if (!raw) return null;
  // Firestore Timestamp
  if (typeof raw?.toDate === "function") return raw.toDate();

  if (typeof raw === "string") {
    // 1) ISO u otra fecha parseable por Date
    const t = Date.parse(raw);
    if (!Number.isNaN(t)) return new Date(t);

    // 2) Formato "D de <mes> de YYYY, hh:mm:ss a.m./p.m. ..."
    const re = /(\d{1,2})\s+de\s+(\w+)\s+de\s+(\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})\s*([ap]\.m\.)/i;
    const m = raw.split(" UTC")[0].match(re);
    if (m) {
      const [, D, mes, YYYY, hh, mm, ss, ampm] = m;
      const monthIdx = {
        enero:0,febrero:1,marzo:2,abril:3,mayo:4,junio:5,
        julio:6,agosto:7,septiembre:8,octubre:9,noviembre:10,diciembre:11
      }[mes.toLowerCase()] ?? 0;
      let H = parseInt(hh,10);
      if (/p\.m\./i.test(ampm) && H < 12) H += 12;
      if (/a\.m\./i.test(ampm) && H === 12) H = 0;
      return new Date(parseInt(YYYY,10), monthIdx, parseInt(D,10), H, parseInt(mm,10), parseInt(ss,10));
    }
  }
  return null; // si no podemos parsear
}



function convertNumberToText(amount) {
  const entero = Math.floor(amount);
  const decimales = Math.round((amount - entero) * 100);
  const enteroText = numberToSpanish(entero);
  return `${enteroText.toUpperCase()} CON ${decimales < 10 ? "0" + decimales : decimales}/100`;
}

// Valida si un teléfono es inválido (más de 10 dígitos o contiene espacios)
function isPhoneInvalid(phone) {
  if (!phone || phone === "Sin teléfono") return false;
  const digits = phone.replace(/\D/g, "");
  return digits.length > 10 || /\s/.test(phone);
}

function formatDateTimeForReport(date, format = "DD/MM/YYYY HH:mm:ss") {
  if (!date) return "";
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format(format) : "";
}

/**
 * Hook: carga los cobros de un día específico desde Firestore.
 * Hace dos consultas en paralelo:
 *   - Primary: where date entre start y end (sirve para docs con date como Timestamp)
 *   - Legacy:  últimos 500 ordenados por createdAt; filtra en memoria los que caen
 *              en el día (rescata docs viejos donde date es string en español).
 * Deduplica por id.
 */
function useCobrosByDay(db, dateStr) {
  const [cobros, setCobros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dateStr) {
      setCobros([]);
      setLoading(false);
      return;
    }

    const start = dayjs(dateStr).startOf("day").toDate();
    const end = dayjs(dateStr).endOf("day").toDate();

    setLoading(true);

    const qPrimary = query(
      collection(db, "cobros"),
      where("date", ">=", start),
      where("date", "<=", end),
      orderBy("date", "desc")
    );

    const qLegacy = query(
      collection(db, "cobros"),
      orderBy("createdAt", "desc"),
      limit(500)
    );

    let lastP = null;
    let lastL = null;

    const merge = () => {
      const map = new Map();
      const push = (docSnap) => {
        const data = docSnap.data();
        const paymentDate =
          (data.date?.toDate ? data.date.toDate() : null) ||
          parsePaymentDate(data.date) ||
          (data.createdAt?.toDate ? data.createdAt.toDate() : null);
        if (!paymentDate) return;
        if (paymentDate < start || paymentDate > end) return;
        const amount = Number(data?.netAmount ?? data?.amount ?? 0);
        map.set(docSnap.id, {
          id: docSnap.id,
          ...data,
          paymentDate,
          amount: Number.isNaN(amount) ? 0 : amount,
        });
      };
      lastP?.docs.forEach(push);
      lastL?.docs.forEach(push);
      setCobros(Array.from(map.values()));
      setLoading(false);
    };

    const u1 = onSnapshot(
      qPrimary,
      (s) => { lastP = s; merge(); },
      (err) => { console.error("useCobrosByDay primary error:", err); setLoading(false); }
    );
    const u2 = onSnapshot(
      qLegacy,
      (s) => { lastL = s; merge(); },
      (err) => { console.error("useCobrosByDay legacy error:", err); }
    );

    return () => { u1(); u2(); };
  }, [db, dateStr]);

  return { cobros, loading };
}


const AdminHome = () => {
  const db = getFirestore();

  // Estados generales
  const [activeBusinesses, setActiveBusinesses] = useState(0);
  const [inactiveBusinesses, setInactiveBusinesses] = useState(0);
  const [agentMapping, setAgentMapping] = useState({});
  const [businessMapping, setBusinessMapping] = useState({});
  const [reports, setReports] = useState([]);

  // Estados para fechas y vista
  const [selectedReportDate, setSelectedReportDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [activeView, setActiveView] = useState("negociosSinCobro");

  // Total mensual (calculado server-side por agregación; no descarga documentos)
  const [monthTotal, setMonthTotal] = useState(0);

  // Estados para filtros y paginación
  const [historicosPage, setHistoricosPage] = useState(1);
  const [reportesPage, setReportesPage] = useState(1);
  const [historicosFilter, setHistoricosFilter] = useState(dayjs().format("YYYY-MM-DD"));
  const [reportesFilter, setReportesFilter] = useState(dayjs().format("YYYY-MM-DD"));

  const [negociosFilterDate, setNegociosFilterDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [negociosPage, setNegociosPage] = useState(1);
  const [negociosFilterStatus, setNegociosFilterStatus] = useState(["cobrado", "conReporte", "sinReporte"]);
  const [negociosFilterActivity, setNegociosFilterActivity] = useState(["activo", "inactivo"]);
  const [negociosReports, setNegociosReports] = useState([]);
  const [negociosSearch, setNegociosSearch] = useState("");
  const [negociosFilterAgent, setNegociosFilterAgent] = useState("todos");

  // Estados para modales de edición e historial en Reporte General
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editBusiness, setEditBusiness] = useState(null);
  const [historialModalOpen, setHistorialModalOpen] = useState(false);
  const [historialBusiness, setHistorialBusiness] = useState(null);
  const [historialCobros, setHistorialCobros] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);

  // Estados de carga para cada suscripción
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingNegocios, setLoadingNegocios] = useState(true);
  const [loadingReports, setLoadingReports] = useState(true);

  // Hooks de cobros por día (consultas dirigidas a Firestore — no descarga histórica completa)
  const todayStr = dayjs().format("YYYY-MM-DD");
  const { cobros: todayCobros } = useCobrosByDay(db, todayStr);
  const { cobros: cobrosForReportDay, loading: loadingCobrosReport } = useCobrosByDay(db, selectedReportDate);
  const { cobros: cobrosForNegociosDay } = useCobrosByDay(db, negociosFilterDate);
  const { cobros: cobrosForHistoricoDay, loading: loadingHistoricoDay } = useCobrosByDay(db, historicosFilter);

  // Escuchar usuarios para agentMapping
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snapshot) => {
      const mapping = {};
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.role === "Cobrador" && data.name) {
          mapping[docSnap.id] = data.name;
        }
      });
      setAgentMapping(mapping);
      setLoadingUsers(false);
    });
    return () => unsub();
  }, [db]);

  // Total mensual del mes en curso, vía agregación server-side (sin descargar docs).
  // Se recalcula cuando se inserta/borra un cobro hoy (hook todayCobros refresca).
  useEffect(() => {
    let cancelled = false;
    const now = new Date();
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
    const endMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    const qMonth = query(
      collection(db, "cobros"),
      where("date", ">=", startMonth),
      where("date", "<=", endMonth)
    );
    getAggregateFromServer(qMonth, { totalSum: sum("netAmount") })
      .then((snap) => {
        if (cancelled) return;
        const v = Number(snap.data().totalSum || 0);
        setMonthTotal(Number.isNaN(v) ? 0 : v);
      })
      .catch((err) => console.error("monthTotal aggregate error:", err));
    return () => { cancelled = true; };
  }, [db, todayCobros]);


  // Escuchar negocios para conteos y mapping
  useEffect(() => {
  const negociosRef = collection(db, "negocios");
  const qNegocios = query(negociosRef, where("status", "in", ["activo", "inactivo"]));

  const unsubscribe = onSnapshot(qNegocios, (snapshot) => {
    let activeCount = 0;
    let inactiveCount = 0;
    const mapping = {};

    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.status === "activo") activeCount++;
      else if (data.status === "inactivo") inactiveCount++;

      const schedule = data.schedule || {};
      const daysArray = Array.isArray(schedule.days) ? schedule.days : [];

      mapping[docSnap.id] = {
        businessName: data.name || "Sin negocio",
        phone: data.phone || "Sin teléfono",
        address: data.address || "",
        agentId: data.agentId,
        owner: data.owner || "Sin dueño",
        type: data.type || "",
        quota: data.quota || "",
        ruta: data.ruta || "",
        days: daysArray.length ? daysArray.join(", ") : "No especificado",
        daysArray,
        openingTime: schedule.openingTime || "No especificado",
        closingTime: schedule.closingTime || "No especificado",
        status: data.status || "desconocido",
      };
    });

    setActiveBusinesses(activeCount);
    setInactiveBusinesses(inactiveCount);
    setBusinessMapping(mapping);
    setLoadingNegocios(false);
  });

  return () => unsubscribe();
}, [db]);


// Escuchar reportes
// Si hay filtro por día lo solicitamos desde Firestore; de lo contrario traemos los 500 más recientes
useEffect(() => {
  const reportesRef = collection(db, "reportes");
  let qReportes;

  if (reportesFilter) {
    const start = dayjs(reportesFilter).startOf("day").toDate();
    const end = dayjs(reportesFilter).endOf("day").toDate();
    qReportes = query(
      reportesRef,
      where("date", ">=", start),
      where("date", "<=", end),
      orderBy("date", "desc")
    );
  } else {
    qReportes = query(reportesRef, orderBy("createdAt", "desc"), limit(500));
  }

  const unsubscribe = onSnapshot(qReportes, (snapshot) => {
    const temp = snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      let createdDate;

      if (typeof data.date === "string") {
        if (/^\d{4}-\d{2}-\d{2}T/.test(data.date)) {
          createdDate = new Date(data.date);
        } else {
          const raw = data.date.split(" UTC")[0];
          const re = /(\d{1,2}) de (\w+) de (\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})\s*([ap]\.m\.)/i;
          const m = raw.match(re);
          if (m) {
            const [, D, mes, YYYY, hh, mm, ss, ampm] = m;
            const monthIdx = {
              enero:0,febrero:1,marzo:2,abril:3,mayo:4,junio:5,
              julio:6,agosto:7,septiembre:8,octubre:9,noviembre:10,diciembre:11
            }[mes.toLowerCase()] || 0;
            let H = parseInt(hh, 10);
            if (/p\.m\./i.test(ampm) && H < 12) H += 12;
            if (/a\.m\./i.test(ampm) && H === 12) H = 0;
            createdDate = new Date(parseInt(YYYY,10), monthIdx, parseInt(D,10), H, parseInt(mm,10), parseInt(ss,10));
          } else {
            createdDate = new Date(data.date);
          }
        }
      } else if (data.date?.toDate) {
        createdDate = data.date.toDate();
      } else if (data.createdAt?.toDate) {
        createdDate = data.createdAt.toDate();
      } else {
        createdDate = new Date();
      }

      return {
        id: docSnap.id,
        businessId: data.businessId || "Sin negocio",
        userId: data.userId || "Sin usuario",
        createdAt: createdDate,
        photoURL: data.photoURL,
        agentId: data.agentId,
        location: data.location,
        address: data.address || ""
      };
    });

    temp.sort((a, b) => b.createdAt - a.createdAt);
    setReports(temp);
    setLoadingReports(false);
  });

  return () => unsubscribe();
}, [db, reportesFilter]);

// Escuchar reportes específicamente para el tab "Reporte General"
useEffect(() => {
  const start = dayjs(negociosFilterDate).startOf("day").toDate();
  const end = dayjs(negociosFilterDate).endOf("day").toDate();
  const qNegReportes = query(
    collection(db, "reportes"),
    where("date", ">=", start),
    where("date", "<=", end),
    orderBy("date", "desc")
  );

  const unsubscribe = onSnapshot(qNegReportes, (snapshot) => {
    const temp = snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      let createdDate;
      if (typeof data.date === "string") {
        if (/^\d{4}-\d{2}-\d{2}T/.test(data.date)) {
          createdDate = new Date(data.date);
        } else {
          const raw = data.date.split(" UTC")[0];
          const re = /(\d{1,2}) de (\w+) de (\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})\s*([ap]\.m\.)/i;
          const m = raw.match(re);
          if (m) {
            const [, D, mes, YYYY, hh, mm, ss, ampm] = m;
            const monthIdx = {
              enero:0,febrero:1,marzo:2,abril:3,mayo:4,junio:5,
              julio:6,agosto:7,septiembre:8,octubre:9,noviembre:10,diciembre:11
            }[mes.toLowerCase()] || 0;
            let H = parseInt(hh, 10);
            if (/p\.m\./i.test(ampm) && H < 12) H += 12;
            if (/a\.m\./i.test(ampm) && H === 12) H = 0;
            createdDate = new Date(parseInt(YYYY,10), monthIdx, parseInt(D,10), H, parseInt(mm,10), parseInt(ss,10));
          } else {
            createdDate = new Date(data.date);
          }
        }
      } else if (data.date?.toDate) {
        createdDate = data.date.toDate();
      } else if (data.createdAt?.toDate) {
        createdDate = data.createdAt.toDate();
      } else {
        createdDate = new Date();
      }
      return {
        id: docSnap.id,
        businessId: data.businessId || "Sin negocio",
        createdAt: createdDate,
        photoURL: data.photoURL,
        agentId: data.agentId,
        location: data.location,
        address: data.address || ""
      };
    });
    setNegociosReports(temp);
  });

  return () => unsubscribe();
}, [db, negociosFilterDate]);



  // Ingresos de hoy (suma sobre los cobros del día actual ya filtrados por el hook)
  const todayPayments = useMemo(
    () => todayCobros.reduce((s, c) => s + (c.amount || 0), 0),
    [todayCobros]
  );

  // Resumen del día seleccionado para el reporte
  const reportDayTotal = useMemo(
    () => cobrosForReportDay.reduce((s, c) => s + (c.amount || 0), 0),
    [cobrosForReportDay]
  );

  const reportDayByAgent = useMemo(() => {
    const map = new Map();
    for (const c of cobrosForReportDay) {
      const agente = agentMapping[c.agentId] || "Sin agente";
      const cur = map.get(agente) || { agente, folios: 0, total: 0 };
      cur.folios += 1;
      cur.total += c.amount || 0;
      map.set(agente, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [cobrosForReportDay, agentMapping]);


// Generar PDF filtrando por DÍA con fechas ya normalizadas
const handleDownloadPDF = useCallback(() => {
  if (!cobrosForReportDay.length) {
    alert("No se encontraron cobros para la fecha seleccionada.");
    return;
  }

  // Agrupa por agente y suma
  const agentData = {};
  for (const p of cobrosForReportDay) {
    const agent = agentMapping[p.agentId] || "N/A";
    if (!agentData[agent]) agentData[agent] = { sum: 0, folios: [] };
    const amt = Number(p.netAmount ?? p.amount ?? 0);
    agentData[agent].sum += isNaN(amt) ? 0 : amt;
    if (p.folio) agentData[agent].folios.push(String(p.folio));
  }

  // 4) Filas para la tabla
  const rows = Object.entries(agentData).map(([agent, info]) => [
    agent,
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(info.sum),
    info.folios.join(", "),
  ]);

  const total = Object.values(agentData).reduce((a, b) => a + b.sum, 0);
  rows.push(["TOTAL", new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(total), ""]);

  // 5) PDF
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Agregar fondo en la primera página
  try {
    doc.addImage(bgPdf, "PNG", 0, 0, pageWidth, pageHeight);
  } catch (e) {
    console.error("Error al cargar fondo página 1:", e);
  }

  const title = "RELACIÓN DE FOLIOS POR CONCEPTO DE COBRO POR USO DE VÍA PÚBLICA";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  const lines = doc.splitTextToSize(title, pageWidth - 20);
  const startY = 50;
  lines.forEach((ln, i) => doc.text(ln, pageWidth / 2, startY + i * 5, { align: "center" }));

  const tableStartY = startY + lines.length * 5 + 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
    dayjs(selectedReportDate).format("D [de] MMMM [de] YYYY").toUpperCase(),
    pageWidth - 10,
    tableStartY - 4,
    { align: "right" }
  );

  // Calcular cuántas filas caben en una página (aproximadamente 18-20 filas por página)
  const maxRowsPerPage = 18;
  const needsSplit = rows.length > maxRowsPerPage;

  if (needsSplit) {
    // Dividir la tabla en dos partes
    const midPoint = Math.ceil(rows.length / 2);
    const firstHalf = rows.slice(0, midPoint);
    const secondHalf = rows.slice(midPoint);

    // Primera tabla (primera mitad)
    doc.autoTable({
      startY: tableStartY,
      head: [["Agente", "Total Cobrado", "Folios"]],
      body: firstHalf,
      styles: { font: "helvetica", fontSize: 10, halign: "center" },
      headStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: "bold", halign: "center" },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "left" } },
      theme: "grid",
      margin: { top: 60, bottom: 50 }
    });

    // Firma después de la primera tabla
    let finalY = doc.lastAutoTable.finalY || tableStartY;
    let signatureY = finalY + 10;
    doc.setLineWidth(0.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.line(60, signatureY, pageWidth - 60, signatureY);
    doc.text("CP. MONICA LIZBETH REYES HERNANDEZ", pageWidth / 2, signatureY + 7, { align: "center" });
    doc.text("JEFA DE INGRESOS", pageWidth / 2, signatureY + 12, { align: "center" });

    // Guardar el PDF actual y crear uno nuevo para la segunda página
    const pdfData1 = doc.output('datauristring');
    
    // Crear nueva página
    doc.addPage();
    
    // Forzar la adición de la imagen en la segunda página
    // Usar el contexto interno de jsPDF
    const pageCount = doc.internal.getNumberOfPages();
    doc.setPage(pageCount);
    
    // Agregar imagen usando el método directo del contexto
    const ctx = doc.internal.pageSize;
    doc.addImage(bgPdf, 'PNG', 0, 0, ctx.width || pageWidth, ctx.height || pageHeight, undefined, 'FAST');
    
    // Agregar título en la segunda página
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    const lines2 = doc.splitTextToSize(title, pageWidth - 20);
    lines2.forEach((ln, i) => doc.text(ln, pageWidth / 2, startY + i * 5, { align: "center" }));
    
    // Agregar fecha en la segunda página
    const tableStartY2 = startY + lines2.length * 5 + 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      dayjs(selectedReportDate).format("D [de] MMMM [de] YYYY").toUpperCase(),
      pageWidth - 10,
      tableStartY2 - 4,
      { align: "right" }
    );

    // Segunda tabla (segunda mitad)
    doc.autoTable({
      startY: tableStartY2,
      head: [["Agente", "Total Cobrado", "Folios"]],
      body: secondHalf,
      styles: { font: "helvetica", fontSize: 10, halign: "center" },
      headStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: "bold", halign: "center" },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "left" } },
      theme: "grid",
      margin: { top: 60, bottom: 50 }
    });

    // Firma después de la segunda tabla
    finalY = doc.lastAutoTable.finalY || tableStartY2;
    signatureY = finalY + 10;
    doc.setLineWidth(0.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.line(60, signatureY, pageWidth - 60, signatureY);
    doc.text("CP. MONICA LIZBETH REYES HERNANDEZ", pageWidth / 2, signatureY + 7, { align: "center" });
    doc.text("JEFA DE INGRESOS", pageWidth / 2, signatureY + 12, { align: "center" });

  } else {
    // Tabla completa en una sola página
    doc.autoTable({
      startY: tableStartY,
      head: [["Agente", "Total Cobrado", "Folios"]],
      body: rows,
      styles: { font: "helvetica", fontSize: 10, halign: "center" },
      headStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: "bold", halign: "center" },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "left" } },
      theme: "grid",
      margin: { top: 60, bottom: 50 }
    });

    // Firma después de la tabla
    const finalY = doc.lastAutoTable.finalY || tableStartY;
    const signatureY = finalY + 10;
    doc.setLineWidth(0.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.line(60, signatureY, pageWidth - 60, signatureY);
    doc.text("CP. MONICA LIZBETH REYES HERNANDEZ", pageWidth / 2, signatureY + 7, { align: "center" });
    doc.text("JEFA DE INGRESOS", pageWidth / 2, signatureY + 12, { align: "center" });
  }

  const fileName = `Reporte_${dayjs(selectedReportDate).format("DD_MMM_YYYY")}.pdf`;
  doc.save(fileName);
}, [cobrosForReportDay, selectedReportDate, agentMapping]);



  // Cálculos para "Reporte General"
  const businessList = useMemo(
    () => Object.entries(businessMapping).map(([id, info]) => ({ id, ...info })),
    [businessMapping]
  );

  // Mapa de cobros del día por negocio (conserva detalle para Excel y estado visual)
  const cobrosDetailByBusinessDay = useMemo(() => {
    const map = {};
    cobrosForNegociosDay.forEach((cobro) => {
      if (!cobro.businessId) return;
      if (!map[cobro.businessId]) {
        map[cobro.businessId] = {
          total: 0,
          count: 0,
          cobros: [],
        };
      }
      map[cobro.businessId].count += 1;
      map[cobro.businessId].total += cobro.amount || 0;
      map[cobro.businessId].cobros.push(cobro);
    });

    Object.values(map).forEach((detail) => {
      detail.cobros.sort((a, b) => (a.paymentDate || 0) - (b.paymentDate || 0));
      detail.firstPaymentDate = detail.cobros[0]?.paymentDate || null;
      detail.lastPaymentDate = detail.cobros[detail.cobros.length - 1]?.paymentDate || null;
      detail.folios = detail.cobros.map((c) => c.folio).filter(Boolean);
      detail.paymentMethods = [
        ...new Set(detail.cobros.map((c) => c.paymentMethod || c.metodoPago).filter(Boolean)),
      ];
    });

    return map;
  }, [cobrosForNegociosDay]);

  const businessesWithChip = useMemo(() => {
    const dayName = dayjs(negociosFilterDate).locale("es").format("dddd");
    const dayNameCapitalized = dayName.charAt(0).toUpperCase() + dayName.slice(1);

    return businessList.map((business) => {
      const matchingReport = negociosReports.find((r) => r.businessId === business.id);
      const cobroDetail = cobrosDetailByBusinessDay[business.id] || { total: 0, count: 0, cobros: [], folios: [], paymentMethods: [] };
      const ingreso = cobroDetail.total || 0;
      const hasCobro = cobroDetail.count > 0;
      const hasReporte = !!matchingReport;
      const isClosed = !business.daysArray.includes(dayNameCapitalized);

      return {
        ...business,
        hasReporte,
        isClosed,
        hasCobro,
        ingreso,
        cobrosCount: cobroDetail.count,
        cobrosDelDia: cobroDetail.cobros,
        firstPaymentDate: cobroDetail.firstPaymentDate,
        lastPaymentDate: cobroDetail.lastPaymentDate,
        paymentFolios: cobroDetail.folios,
        paymentMethods: cobroDetail.paymentMethods,
        reportPhotoURL: matchingReport?.photoURL || null,
        reportDate: matchingReport?.createdAt || null,
        reportAgentId: matchingReport?.agentId || "",
        reportAddress: matchingReport?.address || "",
      };
    });
  }, [businessList, negociosReports, cobrosDetailByBusinessDay, negociosFilterDate]);

  const filteredBusinessesByStatus = useMemo(() => {
    let filtered = businessesWithChip;

    // Filtro por búsqueda
    if (negociosSearch.trim()) {
      const q = negociosSearch.toLowerCase();
      filtered = filtered.filter((b) =>
        (b.businessName && b.businessName.toLowerCase().includes(q)) ||
        (b.owner && b.owner.toLowerCase().includes(q))
      );
    }

    // Filtro por agente
    if (negociosFilterAgent !== "todos") {
      filtered = filtered.filter((b) => b.agentId === negociosFilterAgent);
    }

    // Filtro por actividad (multi-select)
    if (negociosFilterActivity.length > 0 && negociosFilterActivity.length < 2) {
      filtered = filtered.filter((b) => negociosFilterActivity.includes(b.status));
    }

    // Filtro por estado (multi-select)
    if (negociosFilterStatus.length > 0 && negociosFilterStatus.length < 4) {
      filtered = filtered.filter((b) => {
        if (b.hasCobro) return negociosFilterStatus.includes("cobrado");
        if (b.isClosed) return negociosFilterStatus.includes("cerrado");
        if (b.hasReporte) return negociosFilterStatus.includes("conReporte");
        return negociosFilterStatus.includes("sinReporte");
      });
    }

    return filtered;
  }, [businessesWithChip, negociosFilterStatus, negociosFilterActivity, negociosSearch, negociosFilterAgent]);

  // Históricos del día seleccionado (orden desc por hora)
  const filteredHistoricos = useMemo(
    () => [...cobrosForHistoricoDay].sort((a, b) => b.paymentDate - a.paymentDate),
    [cobrosForHistoricoDay]
  );

  const sortedReports = useMemo(() => {
    return [...reports].sort((a, b) => b.createdAt - a.createdAt);
  }, [reports]);

  const filteredReports = useMemo(() => {
    if (!reportesFilter) return sortedReports;
    return sortedReports.filter((r) =>
      dayjs(r.createdAt).format("YYYY-MM-DD") === reportesFilter
    );
  }, [sortedReports, reportesFilter]);

  const handleExportNegocios = useCallback(() => {
    if (!filteredBusinessesByStatus.length) return;

    const rows = filteredBusinessesByStatus.map((business) => ({
      ID: business.id,
      Fecha: dayjs(negociosFilterDate).format("DD/MM/YYYY"),
      Agente: agentMapping[business.agentId] || "Sin agente",
      Negocio: business.businessName,
      Dueño: business.owner || "Sin dueño",
      Teléfono: business.phone || "Sin teléfono",
      Ruta: business.ruta || "Sin ruta",
      Tipo: business.type || "Sin tipo",
      Cuota: business.quota || "",
      Dirección: business.address || "Sin dirección",
      Estado: business.hasCobro
        ? "Cobrado"
        : business.isClosed
        ? "Cerrado"
        : business.hasReporte
        ? "Con reporte"
        : "Sin reporte",
      Actividad: business.status === "activo" ? "Activo" : "Inactivo",
      "Total cobrado": business.ingreso || 0,
      "Cantidad de cobros": business.cobrosCount || 0,
      "Fecha y hora de primer cobro": formatDateTimeForReport(business.firstPaymentDate),
      "Fecha y hora de último cobro": formatDateTimeForReport(business.lastPaymentDate),
      "Fechas de cobro": business.cobrosDelDia
        .map((c) => formatDateTimeForReport(c.paymentDate, "DD/MM/YYYY"))
        .filter(Boolean)
        .join(", "),
      "Horas de cobro": business.cobrosDelDia
        .map((c) => formatDateTimeForReport(c.paymentDate, "HH:mm:ss"))
        .filter(Boolean)
        .join(", "),
      Folios: business.paymentFolios.join(", "),
      "Métodos de pago": business.paymentMethods.length ? business.paymentMethods.join(", ") : "No especificado",
      "Fecha y hora de reporte": formatDateTimeForReport(business.reportDate),
      "Agente que reportó": business.reportAgentId
        ? agentMapping[business.reportAgentId] || business.reportAgentId
        : "",
      "Dirección del reporte": business.reportAddress || "",
      "Foto de reporte": business.reportPhotoURL || "",
      Días: business.days,
      "Horario de apertura": business.openingTime,
      "Horario de cierre": business.closingTime,
    }));

    const detailRows = filteredBusinessesByStatus.flatMap((business) =>
      business.cobrosDelDia.map((cobro) => ({
        "ID negocio": business.id,
        Negocio: business.businessName,
        Dueño: business.owner || "Sin dueño",
        Agente: agentMapping[cobro.agentId] || cobro.agentName || agentMapping[business.agentId] || "Sin agente",
        Folio: cobro.folio || "",
        "Fecha de cobro": formatDateTimeForReport(cobro.paymentDate, "DD/MM/YYYY"),
        "Hora de cobro": formatDateTimeForReport(cobro.paymentDate, "HH:mm:ss"),
        "Fecha y hora de cobro": formatDateTimeForReport(cobro.paymentDate),
        "Importe neto": cobro.amount || 0,
        "Importe total": Number(cobro.totalAmount ?? cobro.amount ?? 0),
        "Crédito aplicado": Number(cobro.appliedCredit ?? 0),
        "Método de pago": cobro.paymentMethod || cobro.metodoPago || "No especificado",
        Tipo: cobro.tipo || "cobro",
        Teléfono: business.phone || "Sin teléfono",
        Ruta: business.ruta || "Sin ruta",
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.min(Math.max(key.length + 4, 14), 34),
    }));
    worksheet["!autofilter"] = { ref: XLSX.utils.encode_range(XLSX.utils.decode_range(worksheet["!ref"])) };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte General");

    if (detailRows.length) {
      const detailWorksheet = XLSX.utils.json_to_sheet(detailRows);
      detailWorksheet["!cols"] = Object.keys(detailRows[0] || {}).map((key) => ({
        wch: Math.min(Math.max(key.length + 4, 14), 30),
      }));
      detailWorksheet["!autofilter"] = { ref: XLSX.utils.encode_range(XLSX.utils.decode_range(detailWorksheet["!ref"])) };
      XLSX.utils.book_append_sheet(workbook, detailWorksheet, "Detalle Cobros");
    }

    const dateLabel = dayjs(negociosFilterDate).format("DD_MM_YYYY");
    XLSX.writeFile(workbook, `reporte_general_${dateLabel}.xlsx`);
  }, [filteredBusinessesByStatus, agentMapping, negociosFilterDate]);

  // Handlers para Reporte General: Editar negocio
  const handleOpenEdit = (business) => {
    setEditBusiness({
      id: business.id,
      name: business.businessName,
      owner: business.owner || "",
      phone: business.phone || "",
      address: business.address || "",
      type: business.type || "",
      quota: business.quota || "",
      agentId: business.agentId || "",
      ruta: business.ruta || "",
      schedule: {
        days: business.daysArray || [],
        openingTime: business.openingTime || "",
        closingTime: business.closingTime || "",
      },
    });
    setEditModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditBusiness((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditScheduleChange = (field, value) => {
    setEditBusiness((prev) => ({
      ...prev,
      schedule: { ...prev.schedule, [field]: value },
    }));
  };

  const handleEditSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      const { id, name, owner, phone, address, type, quota, agentId, ruta, schedule } = editBusiness;
      await updateDoc(doc(db, "negocios", id), {
        name, owner, phone, address, type, quota, agentId, ruta, schedule,
        updatedAt: new Date().toISOString(),
      });
      setEditModalOpen(false);
      setEditBusiness(null);
    } catch (error) {
      console.error("Error actualizando negocio:", error);
    }
  };

  // Handlers para Reporte General: Ver historial
  const handleOpenHistorial = async (business) => {
    setHistorialBusiness(business);
    setHistorialModalOpen(true);
    setHistorialLoading(true);
    try {
      const cobrosQuery = query(
        collection(db, "cobros"),
        where("businessId", "==", business.id),
        orderBy("date", "desc")
      );
      const snapshot = await getDocs(cobrosQuery);
      setHistorialCobros(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error al cargar historial:", error);
      setHistorialCobros([]);
    } finally {
      setHistorialLoading(false);
    }
  };

  // Formateador de moneda
  const formatMXN = (amount) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(amount);

  // Si aún se están cargando algunas colecciones, muestra un spinner animado
  const isLoading = loadingUsers || loadingNegocios || loadingReports;

  const renderContent = () => {
    if (activeView === "diarios") {
      return (
        <>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <label className="font-medium text-gray-700">Día del reporte:</label>
              <input
                type="date"
                value={selectedReportDate}
                max={dayjs().format("YYYY-MM-DD")}
                onChange={(e) => setSelectedReportDate(e.target.value)}
                className="px-2 py-1 border rounded"
              />
            </div>
            <button
              onClick={handleDownloadPDF}
              disabled={loadingCobrosReport || cobrosForReportDay.length === 0}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Descargar PDF {dayjs(selectedReportDate).format("D MMM YYYY")}
            </button>
          </div>

          <div className="bg-white shadow rounded p-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Resumen del {dayjs(selectedReportDate).format("D [de] MMMM [de] YYYY")}
            </h2>
            {loadingCobrosReport ? (
              <p className="text-gray-500 mt-3">Cargando cobros…</p>
            ) : cobrosForReportDay.length === 0 ? (
              <p className="text-gray-500 mt-3">Sin cobros registrados ese día.</p>
            ) : (
              <>
                <p className="text-gray-700 mt-3">
                  Total cobrado:{" "}
                  <strong>{formatMXN(reportDayTotal)}</strong>{" "}
                  · {cobrosForReportDay.length} folio{cobrosForReportDay.length === 1 ? "" : "s"}
                  {" "}· {reportDayByAgent.length} agente{reportDayByAgent.length === 1 ? "" : "s"}
                </p>
                <table className="min-w-full text-sm mt-4 border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-2 px-3 border text-left">Agente</th>
                      <th className="py-2 px-3 border text-right"># Folios</th>
                      <th className="py-2 px-3 border text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportDayByAgent.map(({ agente, folios, total }) => (
                      <tr key={agente} className="border-b">
                        <td className="py-2 px-3 border">{agente}</td>
                        <td className="py-2 px-3 border text-right">{folios}</td>
                        <td className="py-2 px-3 border text-right">{formatMXN(total)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="py-2 px-3 border">TOTAL</td>
                      <td className="py-2 px-3 border text-right">{cobrosForReportDay.length}</td>
                      <td className="py-2 px-3 border text-right">{formatMXN(reportDayTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}
          </div>
        </>
      );
    } else if (activeView === "historicos") {
      const itemsPerPage = 10;
      const totalHistoricosPages = Math.ceil(filteredHistoricos.length / itemsPerPage);
      const historicosPaginated = filteredHistoricos.slice(
        (historicosPage - 1) * itemsPerPage,
        historicosPage * itemsPerPage
      );

      return (
        <div className="bg-white shadow rounded p-4 overflow-x-auto">
          <h2 className="text-lg font-semibold mb-4">Histórico de Ingresos</h2>
          <div className="mb-4 flex items-center gap-3">
            <label className="font-medium text-gray-700">Ver día:</label>
            <input
              type="date"
              value={historicosFilter}
              max={dayjs().format("YYYY-MM-DD")}
              onChange={(e) => {
                setHistoricosFilter(e.target.value);
                setHistoricosPage(1);
              }}
              className="px-2 py-1 border rounded"
            />
            {loadingHistoricoDay && <span className="text-sm text-gray-500">Cargando…</span>}
          </div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-200">
                <th className="py-2 px-4 border">Fecha</th>
                <th className="py-2 px-4 border">Agente</th>
                <th className="py-2 px-4 border">Total Cobrado</th>
                <th className="py-2 px-4 border">Crédito Aplicado</th>
                <th className="py-2 px-4 border">Monto Neto</th>
                <th className="py-2 px-4 border">Folio</th>
              </tr>
            </thead>
            <tbody>
              {historicosPaginated.map((c, i) => (
                <tr key={i} className="border-b">
                  <td className="py-2 px-4 border">
                    {dayjs(c.paymentDate).format("D MMM YYYY, h:mm A")}
                  </td>
                  <td className="py-2 px-4 border">
                    {agentMapping[c.agentId] || c.agentId || "Sin agente"}
                  </td>
                  <td className="py-2 px-4 border">
                    {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                      c.totalAmount || c.amount
                    )}
                  </td>
                  <td className="py-2 px-4 border">
                    {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                      c.appliedCredit || 0
                    )}
                  </td>
                  <td className="py-2 px-4 border">
                    {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                      c.netAmount !== undefined ? c.netAmount : c.amount
                    )}
                  </td>
                  <td className="py-2 px-4 border">{c.folio || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end items-center mt-4">
            <button
              onClick={() => setHistoricosPage(historicosPage - 1)}
              disabled={historicosPage === 1}
              className="px-3 py-1 border rounded mr-2"
            >
              Anterior
            </button>
            <span>
              Página {historicosPage} de {totalHistoricosPages}
            </span>
            <button
              onClick={() => setHistoricosPage(historicosPage + 1)}
              disabled={historicosPage === totalHistoricosPages}
              className="px-3 py-1 border rounded ml-2"
            >
              Siguiente
            </button>
          </div>
        </div>
      );
    } else if (activeView === "reportes") {
      const itemsPerPage = 10;
      const totalReportesPages = Math.ceil(filteredReports.length / itemsPerPage);
      const reportesPaginated = filteredReports.slice(
        (reportesPage - 1) * itemsPerPage,
        reportesPage * itemsPerPage
      );

      return (
        <div className="bg-white shadow rounded p-4 overflow-x-auto">
          <h2 className="text-lg font-semibold mb-4">Historial de Reportes</h2>
          <div className="mb-4 flex items-center">
            <label className="mr-2">Filtrar por día:</label>
            <input
              type="date"
              value={reportesFilter}
              onChange={(e) => {
                setReportesFilter(e.target.value);
                setReportesPage(1);
              }}
              className="px-2 py-1 border rounded"
            />
            {reportesFilter && (
              <button onClick={() => {
                setReportesFilter("");
                setReportesPage(1);
              }} className="ml-2 text-blue-600 underline">
                Limpiar filtro
              </button>
            )}
          </div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-200">
                <th className="py-2 px-4 border">Fecha</th>
                <th className="py-2 px-4 border">Agente</th>
                <th className="py-2 px-4 border">Negocio</th>
                <th className="py-2 px-4 border">Teléfono</th>
                <th className="py-2 px-4 border">Ubicación</th>
                <th className="py-2 px-4 border">Foto</th>
              </tr>
            </thead>
            <tbody>
              {reportesPaginated.map((report) => (
                <tr key={report.id} className="border-b">
                  <td className="py-2 px-4 border">
                    {dayjs(report.createdAt).format("D MMM YYYY, h:mm A")}
                  </td>
                  <td className="py-2 px-4 border">
                    {agentMapping[report.agentId] || report.agentId || "Sin agente"}
                  </td>
                  <td className="py-2 px-4 border">
                    {businessMapping[report.businessId]?.businessName || "Sin negocio"}
                  </td>
                  <td className={`py-2 px-4 border ${isPhoneInvalid(businessMapping[report.businessId]?.phone) ? "text-red-600 font-semibold" : ""}`}>
                    {businessMapping[report.businessId]?.phone || "Sin teléfono"}
                  </td>
                  <td className="py-2 px-4 border">
                    {report.location ? `${report.location.Lat}, ${report.location.Lng}` : "Sin ubicación"}
                  </td>
                  <td className="py-2 px-4 border text-center">
                    {report.photoURL ? (
                      <button onClick={() => openPhotoModal(report.photoURL)} className="text-blue-600 underline">
                        Ver Imagen
                      </button>
                    ) : (
                      "Sin foto"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end items-center mt-4">
            <button
              onClick={() => setReportesPage(reportesPage - 1)}
              disabled={reportesPage === 1}
              className="px-3 py-1 border rounded mr-2"
            >
              Anterior
            </button>
            <span>
              Página {reportesPage} de {totalReportesPages}
            </span>
            <button
              onClick={() => setReportesPage(reportesPage + 1)}
              disabled={reportesPage === totalReportesPages}
              className="px-3 py-1 border rounded ml-2"
            >
              Siguiente
            </button>
          </div>
        </div>
      );
    } else if (activeView === "negociosSinCobro") {
      const itemsPerPage = 15;
      const totalNegociosPages = Math.ceil(filteredBusinessesByStatus.length / itemsPerPage);
      const paginatedBusinesses = filteredBusinessesByStatus.slice(
        (negociosPage - 1) * itemsPerPage,
        negociosPage * itemsPerPage
      );

      return (
        <div className="bg-white shadow rounded p-4 overflow-x-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800">
              Reporte General — {dayjs(negociosFilterDate).format("D [de] MMMM [de] YYYY")}
            </h2>
            <button
              onClick={handleExportNegocios}
              disabled={!filteredBusinessesByStatus.length}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
            >
              Exportar Excel
            </button>
          </div>

          {/* Fila 1: Fecha + Buscador + Agente */}
          <div className="mb-3 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Fecha:</label>
              <input
                type="date"
                value={negociosFilterDate}
                onChange={(e) => { setNegociosFilterDate(e.target.value); setNegociosPage(1); }}
                className="px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-[350px]">
              <FaSearch className="text-gray-400" />
              <input
                type="text"
                placeholder="Buscar negocio o dueño..."
                value={negociosSearch}
                onChange={(e) => { setNegociosSearch(e.target.value); setNegociosPage(1); }}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Agente:</label>
              <select
                value={negociosFilterAgent}
                onChange={(e) => { setNegociosFilterAgent(e.target.value); setNegociosPage(1); }}
                className="px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="todos">Todos</option>
                {Object.entries(agentMapping).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Fila 2: Checkboxes de estado y actividad */}
          <div className="mb-3 flex items-center gap-6 flex-wrap bg-gray-50 rounded px-3 py-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Estado:</span>
              {[
                { value: "cobrado", label: "Cobrado" },
                { value: "conReporte", label: "Con Reporte" },
                { value: "sinReporte", label: "Sin Reporte" },
                { value: "cerrado", label: "Cerrado" },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={negociosFilterStatus.includes(opt.value)}
                    onChange={(e) => {
                      setNegociosFilterStatus((prev) =>
                        e.target.checked ? [...prev, opt.value] : prev.filter((v) => v !== opt.value)
                      );
                      setNegociosPage(1);
                    }}
                    className="accent-blue-500"
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
            <div className="border-l border-gray-300 h-5" />
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Actividad:</span>
              {[
                { value: "activo", label: "Activos" },
                { value: "inactivo", label: "Inactivos" },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={negociosFilterActivity.includes(opt.value)}
                    onChange={(e) => {
                      setNegociosFilterActivity((prev) =>
                        e.target.checked ? [...prev, opt.value] : prev.filter((v) => v !== opt.value)
                      );
                      setNegociosPage(1);
                    }}
                    className="accent-blue-500"
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Totales */}
          <div className="mb-3 flex gap-4 text-sm flex-wrap">
            <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded font-medium">
              Total: {filteredBusinessesByStatus.length}
            </span>
            <span className="bg-green-50 text-green-700 px-3 py-1 rounded">
              Cobrados: {businessesWithChip.filter((b) => b.hasCobro).length}
            </span>
            <span className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded">
              Con reporte: {businessesWithChip.filter((b) => !b.hasCobro && !b.isClosed && b.hasReporte).length}
            </span>
            <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded">
              Sin reporte: {businessesWithChip.filter((b) => !b.hasCobro && !b.isClosed && !b.hasReporte).length}
            </span>
            <span className="bg-red-50 text-red-700 px-3 py-1 rounded">
              Cerrados: {businessesWithChip.filter((b) => !b.hasCobro && b.isClosed).length}
            </span>
          </div>

          {/* Tabla */}
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="py-2 px-3 border text-left">Agente</th>
                <th className="py-2 px-3 border text-left">Negocio</th>
                <th className="py-2 px-3 border text-left">Dueño</th>
                <th className="py-2 px-3 border">Teléfono</th>
                <th className="py-2 px-3 border">Estado</th>
                <th className="py-2 px-3 border">Actividad</th>
                <th className="py-2 px-3 border">Ingreso</th>
                <th className="py-2 px-3 border">Foto</th>
                <th className="py-2 px-3 border">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBusinesses.map((business) => (
                <tr key={business.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 border">
                    {agentMapping[business.agentId] || "Sin agente"}
                  </td>
                  <td className="py-2 px-3 border font-medium">{business.businessName}</td>
                  <td className="py-2 px-3 border">{business.owner}</td>
                  <td className={`py-2 px-3 border text-center ${isPhoneInvalid(business.phone) ? "text-red-600 font-semibold" : ""}`}>
                    {business.phone}
                    {isPhoneInvalid(business.phone) && (
                      <span className="block text-[10px] text-red-400 font-normal">Tel. inválido</span>
                    )}
                  </td>
                  <td className="py-2 px-3 border text-center">
                    {business.hasCobro ? (
                      <span className="bg-green-300 text-green-900 px-2 py-0.5 rounded text-xs font-medium">Cobrado</span>
                    ) : business.isClosed ? (
                      <span className="bg-red-300 text-red-800 px-2 py-0.5 rounded text-xs font-medium">Cerrado</span>
                    ) : business.hasReporte ? (
                      <span className="bg-yellow-300 text-yellow-900 px-2 py-0.5 rounded text-xs font-medium">Reporte</span>
                    ) : (
                      <span className="bg-gray-300 text-gray-800 px-2 py-0.5 rounded text-xs font-medium">Sin Reporte</span>
                    )}
                  </td>
                  <td className="py-2 px-3 border text-center">
                    {business.status === "activo" ? (
                      <span className="text-green-600 text-xs font-medium">Activo</span>
                    ) : (
                      <span className="text-red-600 text-xs font-medium">Inactivo</span>
                    )}
                  </td>
                  <td className="py-2 px-3 border text-center font-medium">
                    {business.ingreso > 0 ? (
                      <span className="text-green-700">{formatMXN(business.ingreso)}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3 border text-center">
                    {business.reportPhotoURL ? (
                      <button onClick={() => openPhotoModal(business.reportPhotoURL)} className="text-blue-600 hover:text-blue-800 underline text-xs">
                        Ver Foto
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3 border text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleOpenEdit(business)}
                        className="text-blue-500 hover:text-blue-700"
                        title="Editar"
                      >
                        <FaEdit size={15} />
                      </button>
                      <button
                        onClick={() => handleOpenHistorial(business)}
                        className="text-gray-500 hover:text-gray-700"
                        title="Ver historial"
                      >
                        <FaEye size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedBusinesses.length === 0 && (
                <tr>
                  <td className="py-4 px-3 border text-center text-gray-500" colSpan={9}>
                    No se encontraron negocios con los filtros seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Paginación */}
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-gray-500">
              Mostrando {paginatedBusinesses.length} de {filteredBusinessesByStatus.length} negocios
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setNegociosPage(negociosPage - 1)}
                disabled={negociosPage === 1}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm">
                Página {negociosPage} de {totalNegociosPages || 1}
              </span>
              <button
                onClick={() => setNegociosPage(negociosPage + 1)}
                disabled={negociosPage === totalNegociosPages || totalNegociosPages === 0}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Modal para foto
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState(null);
  const openPhotoModal = (url) => setSelectedPhotoUrl(url);
  const closePhotoModal = () => setSelectedPhotoUrl(null);

  // Si aún se están cargando datos, muestra un spinner con animación
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <svg
          className="animate-spin h-12 w-12 text-blue-500 mb-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          ></path>
        </svg>
        <span className="text-xl">Cargando...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Panel de Estadísticas</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {/* Cobros de Hoy */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 p-5 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
          <div className="absolute -right-3 -top-3 opacity-15">
            <FaCalendarDay size={80} />
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <FaCalendarDay size={16} className="opacity-80" />
              <span className="text-sm font-medium opacity-90 uppercase tracking-wide">Cobros de Hoy</span>
            </div>
            <p className="text-2xl font-bold mt-2">
              {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(todayPayments)}
            </p>
          </div>
        </div>

        {/* Cobros del Mes */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 p-5 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
          <div className="absolute -right-3 -top-3 opacity-15">
            <FaMoneyBillWave size={80} />
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <FaMoneyBillWave size={16} className="opacity-80" />
              <span className="text-sm font-medium opacity-90 uppercase tracking-wide">Cobros del Mes</span>
            </div>
            <p className="text-2xl font-bold mt-2">
              {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(monthTotal)}
            </p>
          </div>
        </div>

        {/* Comercios Activos */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 p-5 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
          <div className="absolute -right-3 -top-3 opacity-15">
            <FaStore size={80} />
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <FaStore size={16} className="opacity-80" />
              <span className="text-sm font-medium opacity-90 uppercase tracking-wide">Comercios Activos</span>
            </div>
            <p className="text-2xl font-bold mt-2">{activeBusinesses}</p>
          </div>
        </div>

        {/* Comercios Inactivos */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-rose-500 to-rose-700 p-5 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
          <div className="absolute -right-3 -top-3 opacity-15">
            <FaStoreSlash size={80} />
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <FaStoreSlash size={16} className="opacity-80" />
              <span className="text-sm font-medium opacity-90 uppercase tracking-wide">Comercios Inactivos</span>
            </div>
            <p className="text-2xl font-bold mt-2">{inactiveBusinesses}</p>
          </div>
        </div>
      </div>

      {/* Pestañas de navegación */}
      <div className="mb-4 flex justify-center gap-4">
        <button
          onClick={() => setActiveView("negociosSinCobro")}
          className={`px-4 py-2 rounded ${activeView === "negociosSinCobro" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
        >
          Reporte General
        </button>
        <button
          onClick={() => setActiveView("diarios")}
          className={`px-4 py-2 rounded ${activeView === "diarios" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
        >
          Ingresos Diarios
        </button>
        <button
          onClick={() => setActiveView("historicos")}
          className={`px-4 py-2 rounded ${activeView === "historicos" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
        >
          Histórico de Ingresos
        </button>
        <button
          onClick={() => setActiveView("reportes")}
          className={`px-4 py-2 rounded ${activeView === "reportes" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
        >
          Historial de Reportes
        </button>
      </div>

      {renderContent()}

      {/* Modal para foto */}
      {selectedPhotoUrl && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-4 rounded shadow-lg max-w-xl w-full relative">
            <button onClick={closePhotoModal} className="absolute top-2 right-2 text-gray-600">
              Cerrar ✕
            </button>
            <img src={selectedPhotoUrl} alt="Reporte" className="w-full h-auto object-cover rounded" />
          </div>
        </div>
      )}

      {/* Modal Editar Negocio */}
      {editModalOpen && editBusiness && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-11/12 max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Editar Negocio</h3>
              <button onClick={() => { setEditModalOpen(false); setEditBusiness(null); }}>
                <FaTimes className="text-gray-500 hover:text-gray-700" size={18} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-3">
              <div className="flex gap-2">
                <input type="text" name="name" value={editBusiness.name} onChange={handleEditInputChange}
                  placeholder="Nombre del negocio" className="w-1/2 px-3 py-2 border rounded text-sm" required />
                <input type="text" name="owner" value={editBusiness.owner} onChange={handleEditInputChange}
                  placeholder="Dueño" className="w-1/2 px-3 py-2 border rounded text-sm" required />
              </div>
              <div className="flex gap-2">
                <input type="text" name="phone" value={editBusiness.phone} onChange={handleEditInputChange}
                  placeholder="WhatsApp" className={`w-1/2 px-3 py-2 border rounded text-sm ${isPhoneInvalid(editBusiness.phone) ? "border-red-500 text-red-600" : ""}`} required />
                <input type="text" name="type" value={editBusiness.type} onChange={handleEditInputChange}
                  placeholder="Giro comercial" className="w-1/2 px-3 py-2 border rounded text-sm" />
              </div>
              <input type="text" name="address" value={editBusiness.address} onChange={handleEditInputChange}
                placeholder="Dirección del negocio" className="w-full px-3 py-2 border rounded text-sm" />
              <div className="flex gap-2">
                <input type="number" name="quota" value={editBusiness.quota} onChange={handleEditInputChange}
                  placeholder="Cuota" className="w-1/3 px-3 py-2 border rounded text-sm" />
                <select name="agentId" value={editBusiness.agentId} onChange={handleEditInputChange}
                  className="w-1/3 px-3 py-2 border rounded text-sm">
                  <option value="">Seleccionar Agente</option>
                  {Object.entries(agentMapping).map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </select>
                <select name="ruta" value={editBusiness.ruta} onChange={handleEditInputChange}
                  className="w-1/3 px-3 py-2 border rounded text-sm">
                  <option value="">Ruta</option>
                  <option value="1">Ruta 1</option>
                  <option value="2">Ruta 2</option>
                  <option value="3">Ruta 3</option>
                  <option value="4">Ruta 4</option>
                  <option value="5">Ruta 5</option>
                </select>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600 block mb-1">Días de apertura:</span>
                <div className="flex flex-wrap gap-1">
                  {["Lunes","Martes","Miércoles","Jueves","Viernes","Sábado","Domingo"].map((day) => (
                    <label key={day} className="flex items-center gap-1 text-sm cursor-pointer">
                      <input type="checkbox" checked={editBusiness.schedule?.days?.includes(day) || false}
                        onChange={(e) => {
                          const newDays = e.target.checked
                            ? [...(editBusiness.schedule?.days || []), day]
                            : (editBusiness.schedule?.days || []).filter((d) => d !== day);
                          handleEditScheduleChange("days", newDays);
                        }} className="accent-blue-500" />
                      {day}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <input type="time" value={editBusiness.schedule?.openingTime || ""}
                  onChange={(e) => handleEditScheduleChange("openingTime", e.target.value)}
                  className="w-1/2 px-3 py-2 border rounded text-sm" placeholder="Apertura" />
                <input type="time" value={editBusiness.schedule?.closingTime || ""}
                  onChange={(e) => handleEditScheduleChange("closingTime", e.target.value)}
                  className="w-1/2 px-3 py-2 border rounded text-sm" placeholder="Cierre" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setEditModalOpen(false); setEditBusiness(null); }}
                  className="px-4 py-2 border rounded text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded text-sm hover:bg-blue-600">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Historial de Cobros */}
      {historialModalOpen && historialBusiness && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-11/12 max-w-4xl max-h-[85vh] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">
                Historial de Cobros — {historialBusiness.businessName}
              </h3>
              <button onClick={() => { setHistorialModalOpen(false); setHistorialBusiness(null); setHistorialCobros([]); }}>
                <FaTimes className="text-gray-500 hover:text-gray-700" size={18} />
              </button>
            </div>
            {historialLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                <span className="ml-2 text-gray-500">Cargando historial...</span>
              </div>
            ) : historialCobros.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No se encontraron cobros para este negocio.</p>
            ) : (
              <>
                <div className="overflow-auto max-h-[55vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="py-2 px-3 text-left">#</th>
                        <th className="py-2 px-3 text-left">Fecha</th>
                        <th className="py-2 px-3 text-left">Monto</th>
                        <th className="py-2 px-3 text-left">Agente</th>
                        <th className="py-2 px-3 text-left">Folio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historialCobros.map((cobro, i) => {
                        const cobroDate = cobro.date?.toDate ? cobro.date.toDate() : new Date(cobro.date);
                        return (
                          <tr key={cobro.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3">{i + 1}</td>
                            <td className="py-2 px-3">{dayjs(cobroDate).format("D MMM YYYY, h:mm A")}</td>
                            <td className="py-2 px-3 font-semibold text-green-600">
                              {formatMXN(cobro.netAmount || cobro.amount || 0)}
                            </td>
                            <td className="py-2 px-3">{agentMapping[cobro.agentId] || cobro.agentName || "N/A"}</td>
                            <td className="py-2 px-3">{cobro.folio || "N/A"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded flex gap-6 text-sm">
                  <span><strong>Total cobros:</strong> {historialCobros.length}</span>
                  <span><strong>Total cobrado:</strong>{" "}
                    <span className="text-green-600 font-bold">
                      {formatMXN(historialCobros.reduce((sum, c) => sum + (c.netAmount || c.amount || 0), 0))}
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHome;
